import { hasOwnProperty } from "@glideapps/ts-necessities";
import md5 from "blueimp-md5";
import type { AppDescription} from "common/components/SerializedApp";
import { getAppTabs } from "common/components/SerializedApp";
import type {
    ParsedMainScreen,
    ParsedModalScreen,
    ParsedPath,
    ParsedScreen} from "common/generator/components/parsed-path";
import {
    PageScreenPosition,
    WireModalSize,
} from "common/generator/components/parsed-path";
import { getScreenProperty } from "common/generator/components/property-utils";
import { ScreenDescriptionKind, userProfileScreenName } from "common/generator/description";
import { findTabInAppDescription } from "common/generator/description-utils";
import { assertNever, defined } from "common/Support";
import last from "lodash/last";

// Screen prefixes:
//
// s ... primary screen
// m ... small modal
// M ... big modal
//
// After each screen:
//
// r ... row ID

type ScreenPrefix = "s" | "m" | "M";

const specialScreenNames = {
    "sign-in": null,
};
export type SpecialScreenName = keyof typeof specialScreenNames;

export function isSpecialScreenName(screenName: string): screenName is SpecialScreenName {
    return hasOwnProperty(specialScreenNames, screenName);
}

export function getParsedScreens(path: ParsedPath): [ParsedMainScreen, ParsedModalScreen | undefined] {
    let modal: ParsedModalScreen | undefined;
    for (let i = path.screenStack.length - 1; i >= 0; i--) {
        const s = defined(path.screenStack[i]);
        if (s.kind === PageScreenPosition.Main) {
            return [s, modal];
        } else if (s.kind === PageScreenPosition.Modal) {
            if (modal === undefined) {
                modal = s;
            }
        } else {
            return assertNever(s);
        }
    }
    return [path.rootScreen, modal];
}

export function parsedPathCloseModal(path: ParsedPath): ParsedPath {
    const stack = [...path.screenStack];
    while (last(stack)?.kind === PageScreenPosition.Modal) {
        stack.pop();
    }
    return { ...path, screenStack: stack };
}

export function parsedPathRemoveModals(parsedPath: ParsedPath): ParsedPath {
    let { screenStack } = parsedPath;
    screenStack = screenStack.filter(s => s.kind === PageScreenPosition.Main);
    return { ...parsedPath, screenStack };
}

export function parsedPathPush(parsedPath: ParsedPath, screen: ParsedScreen): ParsedPath {
    if (screen.kind === PageScreenPosition.Main) {
        parsedPath = parsedPathRemoveModals(parsedPath);
    }
    return { ...parsedPath, screenStack: [...parsedPath.screenStack, screen] };
}

export function encodeParsedScreenName(screenName: string) {
    return md5(screenName).substring(0, 6);
}

export function unparseParsedScreen(screen: ParsedScreen): readonly string[] {
    const components: string[] = [];

    const prefix = screen.kind === PageScreenPosition.Main ? "s" : screen.size === WireModalSize.Small ? "m" : "M";
    components.push(prefix);
    components.push(encodeParsedScreenName(screen.screenName));
    if (screen.rowID !== undefined) {
        components.push("r", screen.rowID);
    }
    return components;
}

export function normalizeURLPath(urlPath: string): string {
    if (urlPath.startsWith("/")) {
        urlPath = urlPath.substring(1);
    }
    return urlPath;
}

function normalizeSlug(slug: string): string {
    return slug.trim();
}

export function parseURLPath(urlPath: string, appDescription: AppDescription): ParsedPath | undefined {
    const screenNames = [
        ...Object.keys(appDescription.screenDescriptions),
        userProfileScreenName,
        ...Object.keys(specialScreenNames),
    ].map(sn => [sn, md5(sn)] as const);

    urlPath = normalizeURLPath(urlPath);
    const components = urlPath.split("/").map(decodeURIComponent);

    const parseScreenName = () => {
        const hash = components[0];
        if (hash === undefined || hash.length <= 0) return undefined;

        const entry = screenNames.find(([, h]) => h.startsWith(hash));
        if (entry === undefined) return undefined;

        components.shift();

        return entry[0];
    };

    // If I substitute `first()` with its body below, TS thinks that
    // the first check means that `components[0] === "s"` forever
    // after, despite the `unshift`s in between.
    function first() {
        return components[0];
    }

    const parseScreen = <T extends ScreenPrefix>(
        prefix: T
    ): (T extends "s" ? ParsedMainScreen : ParsedModalScreen) | undefined => {
        if (first() !== prefix) return undefined;
        components.shift();

        const screenName = parseScreenName();
        if (screenName === undefined) return undefined;

        let rowID: string | undefined;
        if (first() === "r" && components[1] !== undefined && components[1].length > 0) {
            rowID = components[1];
            components.shift();
            components.shift();
        }

        const base = { screenName, rowID };
        switch (prefix) {
            case "s": {
                const p: ParsedMainScreen = { kind: PageScreenPosition.Main, ...base };
                return p as any;
            }
            case "m": {
                const p: ParsedModalScreen = { kind: PageScreenPosition.Modal, size: WireModalSize.Small, ...base };
                return p as any;
            }
            case "M": {
                const p: ParsedModalScreen = { kind: PageScreenPosition.Modal, size: WireModalSize.Large, ...base };
                return p as any;
            }
            default:
                return assertNever(prefix);
        }
    };

    let tabScreenName: string | undefined;
    for (const tab of getAppTabs(appDescription)) {
        const { slug } = tab;
        if (slug !== undefined && components[0] === normalizeSlug(slug)) {
            tabScreenName = getScreenProperty(tab.screenName);
            if (tabScreenName !== undefined) {
                components.shift();
                break;
            }
        }
    }
    if (tabScreenName === undefined) {
        tabScreenName = parseScreenName();
        if (tabScreenName === undefined) return undefined;
    }

    let rootScreen = parseScreen("s");
    const screenStack: ParsedScreen[] = [];
    if (rootScreen === undefined) {
        rootScreen = { kind: PageScreenPosition.Main, screenName: tabScreenName, rowID: undefined };
    } else {
        for (;;) {
            const prefix = first();
            if (prefix === "s" || prefix === "m" || prefix === "M") {
                const screen = parseScreen(prefix);
                if (screen !== undefined) {
                    screenStack.push(screen);
                    continue;
                }
            }
            break;
        }
    }

    return { tabScreenName, rootScreen, screenStack };
}

export function unparseParsedPath(path: ParsedPath, appDescription: AppDescription): string {
    const components: string[] = [];

    function pushScreenName(screenName: string) {
        components.push(encodeParsedScreenName(screenName));
    }

    const tab = findTabInAppDescription(path.tabScreenName, appDescription)?.[0];
    if (tab?.slug !== undefined) {
        components.push(encodeURIComponent(normalizeSlug(tab.slug)));
    } else {
        pushScreenName(path.tabScreenName);
    }

    components.push(...unparseParsedScreen(path.rootScreen));
    for (const s of path.screenStack) {
        components.push(...unparseParsedScreen(s));
    }

    return components.map(encodeURIComponent).join("/");
}

export function isParsedPathValid(path: ParsedPath, appDescription: AppDescription): boolean {
    if (path.tabScreenName !== userProfileScreenName) {
        if (appDescription.screenDescriptions[path.tabScreenName] === undefined) return false;
        const foundTab = findTabInAppDescription(path.tabScreenName, appDescription)?.[0];
        if (foundTab === undefined || foundTab.hidden) return false;
    }

    const checkScreen = (parsed: ParsedScreen) => {
        if (isSpecialScreenName(parsed.screenName)) return true;

        const screen = appDescription.screenDescriptions[parsed.screenName];
        if (screen === undefined) {
            return parsed.screenName === userProfileScreenName;
        }

        if (screen.kind !== ScreenDescriptionKind.Class) return false;
        if (screen.fetchesData !== true && parsed.rowID === undefined) return false;

        return true;
    };

    if (!checkScreen(path.rootScreen)) return false;
    if (!path.screenStack.every(checkScreen)) return false;

    return true;
}

export function makeRootParsedPath(appDescription: AppDescription): ParsedPath | undefined {
    const tab = getAppTabs(appDescription).find(t => !t.hidden);
    const screenName = getScreenProperty(tab?.screenName);
    if (screenName === undefined) return undefined;

    return {
        tabScreenName: screenName,
        rootScreen: { kind: PageScreenPosition.Main, screenName, rowID: undefined },
        screenStack: [],
    };
}
